// frontend/src/components/cohesion/EntityDrawer.Production.tsx
// Production panel for the universal entity drawer. Reads /api/productions/:id
// for the summary and renders a title-link to the full workspace.

import { useEffect, useState } from 'react'
import { useLanguage } from '../../i18n/index.jsx'
import api from '../../utils/api.js'

type Lang = { t: (k: string) => string }

interface Props {
  id: number
  navigate: (to: string) => void
  closeDrawer: () => void
}

interface Production {
  id: number
  name: string
  status?: string
  show_start?: string | null
  show_end?: string | null
}

export default function EntityDrawerProduction({ id, navigate, closeDrawer }: Props) {
  const { t } = useLanguage() as Lang
  const [data, setData] = useState<Production | null>(null)
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    api.get<Production>(`/productions/${id}`)
      .then((r) => { if (!cancelled) setData(r.data) })
      .catch((e) => { if (!cancelled) setError(String(e?.response?.data?.error ?? e?.message ?? 'load failed')) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  if (loading) return <div className="entity-drawer-loading">{t('loading')}</div>
  if (error)   return <div className="entity-drawer-error" role="alert">{error}</div>
  if (!data)   return <div className="entity-drawer-empty">{t('itemNotFound')}</div>

  const openFull = () => {
    closeDrawer()
    navigate(`/productions/${data.id}`)
  }

  return (
    <>
      <button type="button" className="entity-drawer-title-link" onClick={openFull}>
        {data.name}
      </button>
      <div className="entity-drawer-meta">
        {data.status && (
          <div className="entity-drawer-meta-row">
            <span className="entity-drawer-meta-key">{t('status')}</span>
            <span className="entity-drawer-meta-val">{data.status}</span>
          </div>
        )}
        {data.show_start && data.show_end && (
          <div className="entity-drawer-meta-row">
            <span className="entity-drawer-meta-key">{t('runDates')}</span>
            <span className="entity-drawer-meta-val">
              {new Date(data.show_start).toLocaleDateString()} – {new Date(data.show_end).toLocaleDateString()}
            </span>
          </div>
        )}
      </div>
      <div className="entity-drawer-actions">
        <button type="button" className="entity-drawer-action" onClick={openFull}>
          {t('openWorkspace')}
        </button>
        <button type="button" className="entity-drawer-action" onClick={() => { closeDrawer(); navigate(`/productions/${data.id}?tab=budget`) }}>
          {t('budget')}
        </button>
        <button type="button" className="entity-drawer-action" onClick={() => { closeDrawer(); navigate(`/productions/${data.id}?tab=performances`) }}>
          {t('performancesTab')}
        </button>
      </div>
    </>
  )
}
